import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import {
  FlightLogFormHelper,
  isFixedWingOrUnknown,
  isRotorWingOrUnknown,
} from 'src/app/flight-logs/flight-log-form-helper';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import {
  approachTypes,
  departureTypes,
  LogMode,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { CrewMemberDto } from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { getLengthChanges } from 'src/util/angular/ng-forms-helpers';
import {
  getValidationErrorsChangesDeep,
  getValidationStateChangesDeep,
  toggleErrors,
  toggleValidators,
  ValidationState,
} from 'src/util/angular/ng-forms-validation';
import { kendoDateTime24HourFormat } from 'src/util/dateLib';
import { round, sum } from 'src/util/math';
import { humanize } from 'src/util/text';
import { SelectItem } from 'src/util/ui/forms';
import { UserViewModel } from 'src/view-models/user-view-model';
@Component({
  selector: 'fl-pilot-log-entry',
  templateUrl: './pilot-log-entry.component.html',
  styleUrls: ['./pilot-log-entry.component.scss'],
})
export class PilotLogEntryComponent implements OnInit, OnDestroy {
  @Input()
  public formHelper: FlightLogFormHelper;
  @Input()
  public pilot: CrewMemberDto;
  @Input()
  public pilotLogEntry: FormGroup;

  public validationState$: Observable<ValidationState>;

  public part133$: Observable<number>;

  public approachOptions: SelectItem[] = approachTypes.map((at) => {
    return { value: at, label: humanize(at) };
  });

  public departureOptions: SelectItem[] = departureTypes.map((dt) => {
    return { value: dt, label: humanize(dt) };
  });

  public approachCount$: Observable<number>;
  public departureCount$: Observable<number>;
  public validationErrors$: Observable<string[]>;
  public isFixedWing$: Observable<boolean>;
  public isRotorWing$: Observable<boolean>;
  public blockTime$: Observable<number>;
  public blockStartTime$: Observable<number>;
  public dateTimeFormat = kendoDateTime24HourFormat;
  public componentDestruction$ = new Subject();

  constructor(@Inject(USER) private _user: UserViewModel, private _aircraft: AircraftHttpClient) {}

  get isSimple() {
    return this.formHelper.flight.mode === LogMode.Simple;
  }

  get features() {
    return this._user.flightLogFeatures;
  }

  get approaches() {
    return this.pilotLogEntry.get('approaches') as FormArray;
  }

  get departures() {
    return this.pilotLogEntry.get('departures') as FormArray;
  }
  get part135() {
    return this.pilotLogEntry.get('flightHours.part135') as FormControl;
  }
  get part91() {
    return this.pilotLogEntry.get('flightHours.part91') as FormControl;
  }
  get day() {
    return this.pilotLogEntry.get('flightHours.day') as FormControl;
  }
  get nightUnaided() {
    return this.pilotLogEntry.get('flightHours.nightUnaided') as FormControl;
  }
  get nightAided() {
    return this.pilotLogEntry.get('flightHours.nightAided') as FormControl;
  }
  get blockTime() {
    return this.pilotLogEntry.get('flightHours.blockTime') as FormControl;
  }
  get blockStartTime() {
    return this.pilotLogEntry.get('blockStartTime') as FormControl;
  }
  get blockEndTime() {
    return this.pilotLogEntry.get('blockEndTime') as FormControl;
  }
  public ngOnInit() {
    this.approachCount$ = getLengthChanges(this.approaches);
    this.departureCount$ = getLengthChanges(this.departures);
    this.validationState$ = getValidationStateChangesDeep(this.pilotLogEntry);
    this.validationErrors$ = getValidationErrorsChangesDeep(this.pilotLogEntry);

    const aircraftType$ = this.formHelper.aircraftId$.pipe(
      filter((id) => id != null),
      // tslint:disable-next-line: no-non-null-assertion
      switchMap((id) => this._aircraft.getById(id!)),
      map((aircraft) => (aircraft != null ? aircraft.aircraftType : null)),
    );
    this.isFixedWing$ = aircraftType$.pipe(map(isFixedWingOrUnknown));
    this.isRotorWing$ = aircraftType$.pipe(map(isRotorWingOrUnknown));

    this.isFixedWing$.pipe(distinctUntilChanged()).subscribe((isFixedWing) => {
      toggleValidators((ctrl) => isFixedWing, this.pilotLogEntry, [
        {
          controlName: 'flightHours',
          validators: this._isValidBlockTime(
            this.blockTime,
            this.part135,
            this.part91,
            this.day,
            this.nightAided,
            this.nightUnaided,
          ),
        },
      ]);
    });
  }

  private _isValidBlockTime(
    blockTime: AbstractControl,
    part135: AbstractControl,
    part91: AbstractControl,
    day: AbstractControl,
    nightAided: AbstractControl,
    nightUnaided: AbstractControl,
  ): ValidatorFn {
    return (flightHoursFormGroup: AbstractControl) => {
      const blockTimeHours: number | null = blockTime.value;
      const part91part135HoursSum: number | null = round(sum(part135.value, part91.value), 2);
      const dayNightHoursSum: number | null = round(
        sum(day.value, nightAided.value, nightUnaided.value),
        2,
      );

      toggleErrors(blockTimeHours !== part91part135HoursSum, [blockTime], {
        blockTimeShouldEqualDayNightHours: {
          message: `Block Time hours (${blockTimeHours}) should equal Part 91/135 hours (${part91part135HoursSum}).`,
        },
      });
      toggleErrors(blockTimeHours !== dayNightHoursSum, [blockTime], {
        blockTimeShouldEqualDayNightHours: {
          message: `Block Time hours (${blockTimeHours}) should equal day/night aided/night unaided hours (${dayNightHoursSum}).`,
        },
      });
      return null;
    };
  }

  public getTimeDifference(startTime: Date | null, endTime: Date | null) {
    if (startTime != null && endTime != null) {
      const timeDifferenceInMs = endTime.getTime() - startTime.getTime();
      return this.timeConversion(timeDifferenceInMs);
    }
    return null;
  }

  public timeConversion(duration: number) {
    const msInHour = 1000 * 60 * 60;
    const hours = (duration / msInHour).toFixed(2);
    return hours;
  }

  public ngOnDestroy() {
    this.componentDestruction$.next();
  }
}
